// Rewards referrals + reward credit ledger (migration 404) — admin only.
// Spec: docs/superpowers/specs/2026-11-03-rewards-program-design.md → "Referral lifecycle"

import { apiUrl } from '../lib/api'

export type ReferralStage = 'signed_up' | 'activated' | 'qualified' | 'rejected' | 'reversed'
export type ReferralAction = 'qualify' | 'reject' | 'reverse'

export interface RewardsReferralRow {
  referral_code_id: string
  referrer_user_id: string
  referrer_email: string | null
  referee_user_id: string
  referee_email_masked: string | null
  referee_org_name: string | null
  stage: ReferralStage
  activated_at: string | null
  fee_paid_at: string | null
  fee_refunded: boolean
  days_since_fee: number | null
  shipped_orders: number
  min_days: number
  min_shipped_orders: number
  rewards_usd: string | number
  note: string | null
}

export interface RewardsLedgerRow {
  wallet_event_id: string
  created_at: string
  org_name: string
  kind: 'granted' | 'taken_back'
  source: string | null
  amount_usd: string | number
  expires_at: string | null
  remaining_usd: string | number | null
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      detail?: { message?: string } | string
    }
    const detail = typeof body.detail === 'object' ? body.detail : undefined
    throw new Error(detail?.message ?? `HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

export const referralsKey = ['admin', 'rewards', 'referrals'] as const
export const ledgerKey = ['admin', 'rewards', 'ledger'] as const

export async function fetchRewardsReferrals(): Promise<{ items: RewardsReferralRow[] }> {
  return readJson(await fetch(apiUrl('/admin/rewards/referrals')))
}

export async function actOnReferral(
  refereeUserId: string,
  action: ReferralAction,
  reason: string,
): Promise<RewardsReferralRow> {
  const res = await fetch(
    apiUrl(`/admin/rewards/referrals/${encodeURIComponent(refereeUserId)}/${action}`),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  )
  return readJson(res)
}

export async function fetchRewardsLedger(): Promise<{ items: RewardsLedgerRow[] }> {
  return readJson(await fetch(apiUrl('/admin/rewards/ledger')))
}
